import React from 'react';
import gql from 'graphql-tag';
import { isEqual } from 'lodash';
import track from 'react-tracking';

import bind from '../bind';
import ListingCard from './listing_card';
import ListingRowCard, { CSPListingRowCardExperiment } from './listing_row_card';
import OverflowingRow from './overflowing_row';
import Tiles from './tiles';
import { CoreAdBannerWrapper, ListingsCollection as ListingsCollectionQuery } from '../rql';
import { trackEvent } from '../elog/mparticle_tracker';
import { MParticleEvent, MParticleEventName } from '../elog/mparticle_types';
import { mParticleListingFields } from './with_mparticle_listings';
import { IUserContext, withUserContext } from './user_context_provider';
import { AddToCartButtonFragment } from './add_to_cart_button';
import { shouldShowBumpTag } from './bump_tag';
import { AdCard } from './ads/ad_card';

function componentNameForAd(
  componentName: string,
  listingsCollectionTitle: string,
): string {
  return `${componentName}-${listingsCollectionTitle}`;
}

export interface ExternalProps {
  callToAction?: JSX.Element;
  componentName?: string;
  largeTiles?: boolean;
  listings?: ListingsCollectionQuery.Fragment[];
  bumpedListings?: ListingsCollectionQuery.Fragment[];
  loading?: boolean;
  maxCount?: number;
  title?: JSX.Element | string;
  subtitle?: string;
  subtitleHtml?: string;
  ledeHtml?: string;
  emptyState?: JSX.Element;
  jumplinkSlug?: string;
  onOfferSuccess?: Function;
  displayAsGrid?: boolean;
  displayAsRowCards?: boolean;
  hideMetadataAsOffer?: boolean;
  displayAddToCart?: boolean;
  listingsCount?: number;
  mParticleView?: MParticleEvent;
  showEstimatedMonthlyPayment?: boolean;
  ad?: CoreAdBannerWrapper.Ad;
  titleString?: string;
  useCSPLayoutExperimentCard?: boolean;
}

export type IProps = ExternalProps & IUserContext;

export class ListingsCollection extends React.Component<IProps, null> {
  static defaultProps: Partial<IProps> = {
    maxCount: 12,
    displayAsGrid: false,
    largeTiles: false,
  };

  static fragments = {
    listing: gql`
      fragment ListingsCollection on Listing {
        _id
        id
        bumped
        categoryUuids
        slug
        title
        description
        listingType
        condition {
          displayName
          conditionSlug
        }
        price {
          amountCents
          display
        }
        pricing {
          buyerPrice {
            display
            currency
            amountCents
          }
          originalPrice {
            display
          }
          ribbon {
            display
          }
        }
        images(input: {
          transform: "card_square",
          count: 3,
          scope: "photos",
          type: "Product"
        }) {
          source
        }
        watching
        state
        stateDescription
        shipping {
          shippingPrices {
            shippingMethod
            carrierCalculated
            destinationPostalCodeNeeded
            rate {
              amount
              amountCents
              currency
              display
            }
          }
          freeExpeditedShipping
          localPickupOnly
        },
        shop {
          _id
          address {
            country {
              countryCode
              name
            }
          }
        }
        ...mParticleListingFields
        ...AddToCartButtonData
      }
      ${mParticleListingFields}
      ${AddToCartButtonFragment}
    `,
    bumpKey: gql`
      fragment BumpKey on Listing {
        _id
        bumpKey {
          key
        }
      }
    `,
    financingFields: gql`
      fragment FinancingFields on Listing {
        _id
        estimatedMonthlyPaymentPlan {
          estimatedMonthlyPayment {
            display
          }
        }
      }
    `,
    shopFields: gql`
      fragment ShopFields on Listing {
        _id
        shop {
          _id
          address {
            locality
            countryCode
            region
          }
        }
      }
    `,
  };

  componentDidMount() {
    if (!this.props.loading && this.listings?.length) {
      this.trackView();
      this.trackBumpedImpressions();
    }
  }

  componentDidUpdate(oldProps) {
    if (this.propsDidChange(oldProps)) {
      this.trackView();
      this.trackBumpedImpressions();
    }
  }

  trackView() {
    const { mParticleView } = this.props;
    if (!mParticleView) { return; }

    trackEvent(mParticleView);
  }

  trackBumpedImpressions() {
    if (!this.props.bumpedListings?.length) { return; }
    trackEvent({
      eventName: MParticleEventName.ListingImpression,
      listingIds: this.props.bumpedListings.map(l => l.id),
      listings: this.props.bumpedListings,
      shownAsBumped: true,
      componentName: this.props.componentName,
    });
  }

  propsDidChange(oldProps) {
    return !this.props.loading
      && this.props.listings?.length
      && !isEqual(this.props.listings, oldProps.listings);
  }

  get listings() {
    return (this.props.bumpedListings || []).concat(this.props.listings || []);
  }

  shouldBeSingleRow() {
    if (this.props.displayAsRowCards) return false;
    return !this.props.displayAsGrid;
  }

  isEmpty() {
    return !this.props.loading && !this.listings?.length;
  }

  @bind
  renderListingCard(listing: ListingsCollectionQuery.Fragment, idx) {
    // For tracking purposes, adjust non-bumped rows positions start at 0.
    const bumps = this.props.bumpedListings || [];
    const adjustedIdx = shouldShowBumpTag(listing) ? idx : idx - bumps.length;
    if (this.props.useCSPLayoutExperimentCard) {
      return (
        <CSPListingRowCardExperiment
          key={listing.id}
          listing={listing}
          position={adjustedIdx}
          onOfferSuccess={this.props.onOfferSuccess}
          hideMetadataAsOffer={this.props.hideMetadataAsOffer}
          listingsCount={this.props.listingsCount}
          showEstimatedMonthlyPayment={this.props.showEstimatedMonthlyPayment}
          componentName={this.props.componentName}
        />
      );
    }
    if (this.props.displayAsRowCards) {
      return (
        <ListingRowCard
          key={listing.id}
          listing={listing}
          position={adjustedIdx}
          onOfferSuccess={this.props.onOfferSuccess}
          hideMetadataAsOffer={this.props.hideMetadataAsOffer}
          listingsCount={this.props.listingsCount}
          showEstimatedMonthlyPayment={this.props.showEstimatedMonthlyPayment}
          componentName={this.props.componentName}
        />
      );
    }

    return (
      <ListingCard
        key={listing.id}
        listing={listing}
        position={idx}
        hideMetadataAsOffer={this.props.hideMetadataAsOffer}
        displayAddToCart={this.props.displayAddToCart}
        listingsCount={this.props.listingsCount}
        showEstimatedMonthlyPayment={this.props.showEstimatedMonthlyPayment}
        trackingName={this.props.componentName}
        redesignedCard
      />
    );
  }

  renderPlaceholder() {
    if (this.props.displayAsRowCards) return <ListingRowCard />;
    return <ListingCard />;
  }

  renderTiles() {
    const shouldRenderAd = !this.props.displayAsRowCards && this.props.ad;

    return (
      <Tiles
        singleRow={this.shouldBeSingleRow()}
        placeholder={this.renderPlaceholder()}
        expectedCount={this.props.maxCount}
        loading={this.props.loading}
        largeTiles={this.props.largeTiles && !this.props.displayAsRowCards}
        oneWide={this.props.displayAsRowCards}
        emptyState={this.props.emptyState}
      >
        {
          shouldRenderAd &&
          <AdCard
            ad={this.props.ad}
            key={this.props.ad.uuid}
            componentName={
              componentNameForAd(
                this.props.componentName,
                this.props.titleString,
              )
            }
          />
        }
        {this.listings.map(this.renderListingCard)}
      </Tiles>
    );
  }

  render() {
    if (this.isEmpty() && !this.props.emptyState) {
      return null;
    }

    return (
      <OverflowingRow
        title={this.props.title}
        subtitle={this.props.subtitle}
        subtitleHtml={this.props.subtitleHtml}
        ledeHtml={this.props.ledeHtml}
        action={!this.isEmpty() && this.props.callToAction}
        collectionCount={this.listings?.length}
        appendAction={this.props.displayAsGrid || this.props.displayAsRowCards}
        id={this.props.jumplinkSlug}
      >
        {this.renderTiles()}
      </OverflowingRow>
    );
  }
}

// TODO - set componentName in tracking context more directly where we actually need it
const withTrackingListingsCollection = track((props: IProps) => ({ componentName: props.componentName }))(ListingsCollection);

export default withUserContext<ExternalProps>(withTrackingListingsCollection);
